import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';

import {
    Sizes,
    Checkmark,
    noticeError,
    TooltipAnchor,
    TOOLTIP_END_ALIGNMENT,
    TOOLTIP_BOTTOM_LOCATION,
    DefaultIntlFormatValues,
} from '@ratehub/base-ui';

import APPROVAL_LIKELIHOOD from '../definitions/ApprovalLikelihood';
import APPROVAL_LIKELIHOOD_COLOURS from '../definitions/ApprovalLikelihoodColours';


function ApprovalLikelihoodPill({ approvalLikelihood, className }) {
    if (!Object.values(APPROVAL_LIKELIHOOD).includes(approvalLikelihood)) {
        noticeError(new RangeError(`Unexpected approvalLikelihood value: “${approvalLikelihood}”`), {
            expectedValues: `Expected values are: [ “${Object.values(APPROVAL_LIKELIHOOD).map(al => al).join('”, “')}” ]`,
        });

        return null;
    }

    return (
        <Container
            className={classNames(className,
                'rh-display-flex rh-text-align-center rh-justify-content-center',
                'rh-mb-0_5 rh-py-0_5 rh-px-0_25 rh-border-radius-8px leading-xs',
                {
                    'rh-text-xs': approvalLikelihood === APPROVAL_LIKELIHOOD.PREQUALIFIED,
                    'rh-text-2xs': approvalLikelihood !== APPROVAL_LIKELIHOOD.PREQUALIFIED,
                }
            )}
            approvalLikelihood={approvalLikelihood}
            data-test-name="approval-likelihood-pill"
        >
            <FormattedMessage
                id="cardfinder.bnb.approval-likelihood"
                defaultMessage={`<strong>
                    {approvalLikelihood, select,
                        prequalified {<prequalified>You’re prequalified!</prequalified>}
                        other {Approval likelihood: <bolder>{
                            approvalLikelihood, select,
                                excellent {EXCELLENT}
                                good {GOOD}
                                fair {FAIR}
                                poor {POOR}
                                other {N/A}
                            }</bolder>
                        }
                    }
                    </strong>`}
                values={{
                    ...DefaultIntlFormatValues,
                    approvalLikelihood: approvalLikelihood,
                    // eslint-disable-next-line react/jsx-one-expression-per-line
                    bolder: str => <span className="weight-bold">{str}</span>,
                    prequalified: str => (
                        // due to the <Checkmark /> the overall height of this pill will be slightly taller than the others
                        <span className="rh-display-inline-flex rh-align-items-center rh-justify-content-center">
                            <Checkmark
                                className="rh-mr-0_25"
                                stroke={APPROVAL_LIKELIHOOD_COLOURS.prequalified.stroke}
                                width="1.5em"
                                height="1.5em"
                            />
                            <span className="rh-mr-0_25">
                                {str}
                            </span>
                            <TooltipAnchor
                                className="rh-ml-0_25"
                                // buttonSize of 1.25rem seems right in this context, but it is not supported
                                // this is 1rem; XS is 1.5rem and looks unwieldy
                                buttonSize={Sizes.ICONS['2XS']}
                                message={(
                                    <FormattedMessage
                                        id="edb.cc.prequalified.tooltip"
                                        defaultMessage="Based on your TransUnion credit report, you are pre-qualified. You can apply confidently, but approval is not guaranteed."
                                    />
                                )}
                                nubbinAlignment={TOOLTIP_END_ALIGNMENT}
                                tooltipLocation={TOOLTIP_BOTTOM_LOCATION}
                                tooltipWidth="21em"
                                data-name="edb.cc.prequalified.tooltip"
                            />
                        </span>
                    ),
                }}
            />
        </Container>
    );
}

ApprovalLikelihoodPill.propTypes = {
    approvalLikelihood: PropTypes.oneOf(Object.values(APPROVAL_LIKELIHOOD)),
    className: PropTypes.string,
};

const Container = styled.div`
    ${({ approvalLikelihood }) => `
        // cannot use getBGColourClassName() on Container since one of the colours needs rgba()
        background-color: ${APPROVAL_LIKELIHOOD_COLOURS[approvalLikelihood]?.backgroundColour};
    `};
`;


export default ApprovalLikelihoodPill;
